import { Injectable, Injector } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    DetachedRouteHandle,
    NavigationEnd,
    Router,
    RouteReuseStrategy,
} from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { NavigationPairResolverService } from './service/navigation-pair-resolver.service';
import { RouteCacheManagerService } from './service/route-cache-manager.service';
import { StatePersistenceService } from './service/state-persistence.service';
import { LayoutService } from '../layout/layout.service';

/**
 * CustomRouteReuseStrategy
 *
 * Implements Angular's RouteReuseStrategy contract to provide path-pair–driven
 * in-memory DOM reuse.
 *
 * Key rules:
 *  - Detach (cache) ONLY when the route is a configured fromPath and the
 *    user is navigating to the matching toPath.
 *  - Reattach ONLY when a cached handle exists and the user is returning
 *    from the matching toPath.
 *  - Detail routes (:id params) are never listed as fromPath entries in the
 *    constants, so they are structurally excluded from caching (FR-7).
 *  - Any navigation not matching a configured pair clears the cache (FR-4).
 *  - sessionStorage persistence is triggered on every valid detach (FR-2).
 *
 * ── Scroll Preservation ─────────────────────────────────────────────────────
 *  Components that need scroll restored after back-navigation only need to add
 *  the attribute data-preserve-scroll to their scrollable element:
 *
 *    <div data-preserve-scroll ...>...</div>
 *
 *  No TypeScript changes are required in individual components; all save/restore
 *  logic lives here.
 *
 *  How it works:
 *   1. shouldDetach() is called BEFORE Angular removes the DOM, so scrollTop
 *      can be read directly from document.querySelector('[data-preserve-scroll]').
 *   2. shouldAttach() tracks which routeKey was just reattached.
 *   3. On NavigationEnd the strategy sets scrollTop via double-rAF to ensure
 *      Angular has finished reattaching the view before we write to the DOM.
 *
 * WHY Injector for Router:
 *   This class is provided as RouteReuseStrategy, which Angular reads during
 *   Router construction. Injecting Router directly would create:
 *   Router → RouteReuseStrategy → Router  (NG0200).
 *   Router is resolved lazily via a private getter instead.
 */
@Injectable()
export class CustomRouteReuseStrategy implements RouteReuseStrategy {
    private _router: Router | null = null;
    private routerSub: Subscription | null = null;

    /** scrollTop values saved per routeKey just before the DOM is detached. */
    private readonly scrollPositionMap = new Map<string, number>();

    /**
     * Set by shouldAttach() when it returns true; consumed by the NavigationEnd
     * handler to know which route was just reattached and needs scroll restored.
     */
    private lastAttachedKey: string | null = null;

    constructor(
        private readonly injector: Injector,
        private readonly pairResolver: NavigationPairResolverService,
        private readonly cacheManager: RouteCacheManagerService,
        private readonly statePersistence: StatePersistenceService,
        private readonly layoutService: LayoutService,
    ) { }

    // ── Lazy Router ───────────────────────────────────────────────────────────

    /**
     * Router is resolved on first access, guaranteeing the DI graph is fully
     * initialised before we touch Router (prevents NG0200 circular dep).
     *
     * The NavigationEnd subscription here drives the scroll restore: after Angular
     * reattaches a cached component we wait two animation frames (double-rAF) to
     * ensure the browser has painted the view, then write scrollTop.
     */
    private get router(): Router {
        if (!this._router) {
            this._router = this.injector.get(Router);
            this.routerSub = this._router.events
                .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
                .subscribe(() => this.onNavigationEnd());
        }
        return this._router;
    }

    // ── Scroll helpers ────────────────────────────────────────────────────────

    /**
     * Called by the NavigationEnd subscriber; restores scroll for the route
     * that was just reattached (tracked in lastAttachedKey).
     */
    private onNavigationEnd(): void {
        if (!this.lastAttachedKey) {
            return;
        }
        const key = this.lastAttachedKey;
        this.lastAttachedKey = null;

        const savedTop = this.scrollPositionMap.get(key);
        if (savedTop === undefined) {
            return;
        }

        // Double-rAF: first frame lets Angular commit the reattached view;
        // second frame lets the browser paint before we set scrollTop.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const el = document.querySelector('[data-preserve-scroll]') as HTMLElement | null;
                if (el) {
                    el.scrollTop = savedTop;
                }
            });
        });
    }

    // ── shouldReuseRoute ──────────────────────────────────────────────────────

    /**
     * Default Angular behaviour: reuse the route when the route configuration
     * object is the same reference (i.e. navigating to the exact same path).
     */
    shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot,
    ): boolean {
        return future.routeConfig === curr.routeConfig;
    }

    // ── shouldDetach ──────────────────────────────────────────────────────────

    /**
     * Returns true — instructing Angular to detach and hand us the component —
     * only when:
     *   1. The route's key is a configured fromPath, AND
     *   2. The *current in-progress navigation's destination* is a valid toPath.
     *
     * SCROLL SAVE:
     *   shouldDetach is called BEFORE Angular removes the component's DOM from
     *   the document. This is the only reliable point where scrollTop is still
     *   valid. We read [data-preserve-scroll] from the live document and cache
     *   the value here (not in NavigationStart, which fires again on back-nav
     *   when the component is already detached and scrollTop is 0).
     *
     * FIX – URL timing bug:
     *   NavigationPairResolverService.getCurrentUrl() is updated on NavigationEnd,
     *   which fires AFTER shouldDetach. Reading it here would give the destination
     *   of the PREVIOUS navigation. Instead we call Router.getCurrentNavigation()
     *   which is available synchronously during the navigation lifecycle.
     *
     * All other navigations discard any stale cache for this path (FR-4).
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        const routeKey = this.getPath(route);

        // Read the real destination from the in-progress navigation.
        const nav = this.router.getCurrentNavigation();
        const destinationUrl = nav
            ? this.router.serializeUrl(nav.extractedUrl)
            : this.pairResolver.getCurrentUrl() ?? '';

        const isLeavingFromPath = this.pairResolver.isValidFromPath(routeKey ?? '');
        const isHeadingToValidPair = this.pairResolver.isValidToPath(destinationUrl);

        if (isLeavingFromPath && isHeadingToValidPair) {
            // DOM is still in document here — snapshot scroll before detach.
            const scrollEl = document.querySelector('[data-preserve-scroll]') as HTMLElement | null;
            if (scrollEl && routeKey) {
                this.scrollPositionMap.set(routeKey, scrollEl.scrollTop);
            }
            return true;
        }

        // Invalid navigation — clear any stale cached state (FR-4).
        if (routeKey) {
            this.cacheManager.clear(routeKey);
            this.statePersistence.clear(routeKey);
        }
        return false;
    }

    // ── store ─────────────────────────────────────────────────────────────────

    /**
     * Called by Angular immediately after shouldDetach returns true.
     * Stores the handle in memory and triggers sessionStorage persistence (FR-2).
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        const routeKey = this.getPath(route);
        if (!routeKey || handle === null) {
            return;
        }
        this.cacheManager.store(routeKey, handle);

        if (!this.statePersistence.restore(routeKey)) {
            this.statePersistence.persist(routeKey, {});
        }
    }

    // ── shouldAttach ──────────────────────────────────────────────────────────

    /**
     * Returns true — instructing Angular to reattach a cached component — only
     * when:
     *   1. A cached handle exists for this route key, AND
     *   2. The user is returning from a valid toPath (FR-3).
     *
     * When returning true we record lastAttachedKey so the NavigationEnd handler
     * knows which route was reattached and can restore its scroll position.
     *
     * WHY getCurrentUrl, not getPreviousUrl:
     *   During back-navigation, NavigationEnd has NOT fired yet, so previousUrl /
     *   currentUrl still hold values from the last completed navigation:
     *     previousUrl = 'servicenear/dashboard'     ← list page (wrong to check)
     *     currentUrl  = 'servicenear/serviceDetails' ← detail page (correct)
     *   We check currentUrl to verify we are returning FROM a valid toPath.
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const routeKey = this.getPath(route);
        if (!routeKey) {
            return false;
        }

        const hasCachedHandle = this.cacheManager.has(routeKey);
        const returningFromToPath = this.pairResolver.isValidToPath(
            this.pairResolver.getCurrentUrl() ?? '',
        );

        if (hasCachedHandle && returningFromToPath) {
            this.lastAttachedKey = routeKey;
            this.layoutService.notifyRouteReattached(routeKey);
            return true;
        }
        return false;
    }

    // ── retrieve ──────────────────────────────────────────────────────────────

    /**
     * Returns the stored DetachedRouteHandle for the given route key.
     * Angular will reattach this handle to the DOM.
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const routeKey = this.getPath(route);
        if (!routeKey) {
            return null;
        }
        return this.cacheManager.retrieve(routeKey);
    }

    // ── Private helpers ───────────────────────────────────────────────────────

    /**
     * Derives a stable cache key from the route snapshot using the actual
     * matched URL segments — NOT routeConfig.path.
     *
     * WHY: routeConfig.path is the route template string (e.g. 'dashboard').
     * For modules that are eagerly imported into AppModule their routes are
     * registered at root level, so routeConfig.path never contains the parent
     * module's URL prefix (e.g. 'servicenear'). Using routeConfig.path would
     * produce the key 'dashboard' while the constants and NavigationEnd URLs
     * both use 'servicenear/dashboard' — causing a permanent cache miss.
     *
     * route.pathFromRoot[n].url contains the UrlSegments actually matched for
     * each level of the snapshot tree. Joining those always reflects the real
     * browser URL path, regardless of eager vs lazy module loading.
     */
    private getPath(route: ActivatedRouteSnapshot): string | null {
        const key = route.pathFromRoot
            .reduce((acc, r) => acc.concat(r.url), [] as import('@angular/router').UrlSegment[])
            .map(segment => segment.path)
            .filter(path => path.length > 0)
            .join('/');
        return key || null;
    }
}
